import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Link2, ShieldAlert, CheckCircle2, AlertTriangle, Wand2, Save, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { fetchInternalLinks } from "@/lib/internalLinks";
import RelatedLinksPicker from "@/components/admin/RelatedLinksPicker";

/**
 * Internal Linking Manager: approved targets, prohibited rules, a site-wide link audit,
 * one-click recommended links, and Related Services for the built-in Commercial page.
 */
export default function AdminInternalLinks() {
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [commercial, setCommercial] = useState([]);
  const [recommended, setRecommended] = useState({});
  const [savingCommercial, setSavingCommercial] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, c, r] = await Promise.all([
        api.get("/admin/internal-links/audit"),
        api.get("/admin/page-links/commercial-cleaning"),
        api.get("/admin/internal-links/recommendations"),
      ]);
      setAudit(a.data);
      setCommercial(c.data?.related_links || []);
      setRecommended(r.data?.related || {});
      fetchInternalLinks(true);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const applyRecommended = async () => {
    if (!window.confirm(
      "Apply the recommended links?\n\n• Service cards: Learn More → matching service page, Estimate → /request-estimate\n" +
      "• Landing pages: Related Services filled in where none are selected yet\n\nNo pages are created, published or unpublished.",
    )) return;
    setApplying(true);
    try {
      const { data } = await api.post("/admin/internal-links/apply-recommended");
      const c = data.changed || {};
      toast.success(`Updated ${c.services?.length || 0} service cards and ${c.landing_pages?.length || 0} landing pages.`);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
    setApplying(false);
  };

  const saveCommercial = async () => {
    setSavingCommercial(true);
    try {
      const { data } = await api.put("/admin/page-links/commercial-cleaning", { related_links: commercial });
      setCommercial(data.related_links || []);
      toast.success("Commercial Cleaning related services saved.");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
    setSavingCommercial(false);
  };

  const issues = audit?.issues || [];
  const errors = issues.filter((i) => i.level === "error");
  const warnings = issues.filter((i) => i.level === "warning");

  return (
    <div data-testid="admin-internal-links-page" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-display font-bold text-[#0F172A] flex items-center gap-2">
            <Link2 className="w-6 h-6 text-[#1E3A8A]" aria-hidden="true" /> Internal Links
          </h2>
          <p className="text-sm text-[#475569] mt-1 max-w-2xl">
            Link service cards, landing pages, About, and FAQ text to approved pages only. In any text box,
            highlight words and pick a page, or click a suggestion. Links to pages that aren&apos;t published stay hidden on
            the live site.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} data-testid="il-refresh" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full border border-slate-200 bg-white text-sm font-semibold">
            <RefreshCw className="w-4 h-4" aria-hidden="true" /> Re-check
          </button>
          <button
            onClick={applyRecommended}
            disabled={applying || loading}
            data-testid="il-apply-recommended"
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-semibold hover:bg-[#1E40AF] disabled:opacity-60"
          >
            <Wand2 className="w-4 h-4" aria-hidden="true" /> {applying ? "Applying…" : "Apply recommended links"}
          </button>
        </div>
      </div>

      {/* Link check */}
      <section className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="il-audit">
        <h3 className="font-display font-semibold text-[#0F172A]">Link check</h3>
        {loading && !audit ? (
          <p className="mt-2 text-sm text-[#475569]">Checking…</p>
        ) : issues.length === 0 ? (
          <p className="mt-2 text-sm text-emerald-700 flex items-center gap-1.5" data-testid="il-audit-clean">
            <CheckCircle2 className="w-4 h-4" aria-hidden="true" /> No link problems found.
          </p>
        ) : (
          <>
            <p className="mt-1 text-sm text-[#475569]" data-testid="il-audit-counts">
              <span className="font-semibold text-rose-700">{errors.length} to fix</span> ·{" "}
              <span className="font-semibold text-amber-700">{warnings.length} to review</span>
            </p>
            <ul className="mt-3 divide-y divide-slate-100">
              {[...errors, ...warnings].map((i, n) => (
                <li key={n} className="py-2.5 flex items-start gap-3 text-sm" data-testid={`il-issue-${n}`}>
                  {i.level === "error" ? (
                    <ShieldAlert className="w-4 h-4 mt-0.5 text-rose-600 shrink-0" aria-hidden="true" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" aria-hidden="true" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-[#0F172A]">
                      <span className="font-semibold">{i.area}</span> · {i.item} · <span className="text-slate-500">{i.where}</span>
                    </p>
                    <p className={i.level === "error" ? "text-rose-700" : "text-amber-700"}>
                      {i.anchor ? `“${i.anchor}” ` : ""}{i.url ? `${i.url}: ` : ""}{i.message}
                    </p>
                  </div>
                  {i.edit_path && (
                    <Link to={i.edit_path} className="shrink-0 text-xs font-semibold text-[#1E3A8A] hover:underline">Edit</Link>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Approved targets */}
        <section className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="il-approved">
          <h3 className="font-display font-semibold text-[#0F172A]">Approved pages</h3>
          <p className="text-xs text-slate-500 mt-1">Only these pages can be chosen in link fields and text links.</p>
          <ul className="mt-3 divide-y divide-slate-100">
            {(audit?.targets || []).map((t) => (
              <li key={t.url} className="py-2 flex items-center justify-between gap-3 text-sm" data-testid={`il-target-${t.url.replace("/", "")}`}>
                <span className="font-medium text-[#0F172A]">{t.label}</span>
                <span className="flex items-center gap-2">
                  <code className="text-xs text-slate-600">{t.url}</code>
                  {t.live ? (
                    <span className="text-[10px] font-semibold uppercase text-emerald-700">Live</span>
                  ) : (
                    <span className="text-[10px] font-semibold uppercase text-amber-700">Not published</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </section>

        <div className="space-y-6">
          {/* Prohibited */}
          <section className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="il-prohibited">
            <h3 className="font-display font-semibold text-[#0F172A]">Blocked links</h3>
            <p className="text-xs text-slate-500 mt-1">These can&apos;t be saved anywhere; existing ones show in the link check.</p>
            <ul className="mt-3 space-y-1 text-sm text-[#475569] list-disc pl-5">
              {(audit?.prohibited || []).map((p) => <li key={p}><code className="text-xs">{p}</code></li>)}
              <li>Links with double slashes, spaces, capitals, ? query strings, or pages not on the approved list</li>
            </ul>
          </section>

          {/* Commercial page related */}
          <section className="bg-white rounded-2xl border border-slate-100 p-5" data-testid="il-commercial">
            <h3 className="font-display font-semibold text-[#0F172A]">Commercial Cleaning page: Related Services</h3>
            <div className="mt-3">
              {loading && !audit ? <p className="text-sm text-[#475569]">Loading…</p> : (
              <RelatedLinksPicker
                value={commercial}
                onChange={setCommercial}
                selfUrl="/commercial-cleaning"
                recommended={recommended["/commercial-cleaning"]}
                testid="il-commercial-related"
              />
              )}
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={saveCommercial}
                disabled={savingCommercial || loading}
                data-testid="il-commercial-save"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#1E3A8A] text-white text-sm font-semibold disabled:opacity-60"
              >
                <Save className="w-4 h-4" aria-hidden="true" /> Save
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
